// Shared types used by both server and client.

export const TIMEFRAMES = ["today", "week", "month", "3mo", "6mo", "12mo", "all"] as const;
export type Timeframe = (typeof TIMEFRAMES)[number];

export const INTERVALS = ["1m", "1h", "1d", "1w"] as const;
export type Interval = (typeof INTERVALS)[number];

export const TIMEFRAME_LABELS: Record<Timeframe, string> = {
  today: "Idag",
  week: "Vecka",
  month: "Månad",
  "3mo": "3 mån",
  "6mo": "6 mån",
  "12mo": "12 mån",
  all: "Allt",
};

export const INTERVAL_LABELS: Record<Interval, string> = {
  "1m": "1 min",
  "1h": "1 tim",
  "1d": "1 dag",
  "1w": "1 vecka",
};

/** Which candle intervals are valid for each period (avoids huge/invalid requests). */
export const ALLOWED_INTERVALS: Record<Timeframe, Interval[]> = {
  today: ["1m", "1h"],
  week: ["1m", "1h", "1d"],
  month: ["1h", "1d"],
  "3mo": ["1d", "1w"],
  "6mo": ["1d", "1w"],
  "12mo": ["1d", "1w"],
  all: ["1d", "1w"],
};

export const DEFAULT_INTERVAL: Record<Timeframe, Interval> = {
  today: "1m",
  week: "1h",
  month: "1d",
  "3mo": "1d",
  "6mo": "1d",
  "12mo": "1d",
  all: "1w",
};

/** Clamp an interval to one valid for the given period. */
export function resolveInterval(tf: Timeframe, interval: Interval | undefined): Interval {
  if (interval && ALLOWED_INTERVALS[tf].includes(interval)) return interval;
  return DEFAULT_INTERVAL[tf];
}

export const TICKER = "TERRNT-B.ST";

/** A single OHLCV bar. `t` is unix seconds (UTC) at bar start. */
export interface Candle {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

export type MarketState = "PRE" | "REGULAR" | "POST" | "CLOSED";

export interface Quote {
  price: number;
  currency: string;
  marketState: MarketState;
  /** Yahoo/Avanza market timestamp, unix seconds. */
  marketTime: number;
  /** When we fetched it, unix seconds. */
  fetchedAt: number;
  previousClose?: number;
  dayChange?: number;
  dayChangePct?: number;
  /** Best bid (buy) — top of order book. */
  bid?: number;
  /** Best ask (sell) — top of order book. */
  ask?: number;
  /** Bid/ask spread as a percentage. */
  spreadPct?: number;
  /** Today's traded volume (shares). */
  dayVolume?: number;
  /** Today's high / low. */
  dayHigh?: number;
  dayLow?: number;
  /** Volume-weighted average price for the day. */
  vwap?: number;
  source: string;
}

export interface CandlesResponse {
  timeframe: Timeframe;
  interval: Interval;
  candles: Candle[];
  currency: string;
  source: string;
  /** True when served from cache after an upstream failure. */
  stale: boolean;
}

export type SignalType = "buy" | "sell";

export interface Signal {
  /** unix seconds, matches a candle `t`. */
  t: number;
  type: SignalType;
  /** Human-readable reason, e.g. "EMA9 crossed above EMA21". */
  reason: string;
  /** Number of indicators agreeing (1-3). 2+ is "strong". */
  strength: number;
}

export interface IndicatorPoint {
  t: number;
  value: number | null;
}

export interface MacdPoint {
  t: number;
  macd: number | null;
  signal: number | null;
  histogram: number | null;
}

export interface IndicatorsResponse {
  timeframe: Timeframe;
  ema9: IndicatorPoint[];
  ema21: IndicatorPoint[];
  rsi: IndicatorPoint[];
  macd: MacdPoint[];
  signals: Signal[];
}

export interface AccountPnl {
  id: number;
  label: string;
  shares: number;
  avgCost: number;
  cost: number;
  marketValue: number;
  unrealized: number;
  unrealizedPct: number;
}

export interface Portfolio {
  currency: string;
  price: number;
  accounts: AccountPnl[];
  total: {
    shares: number;
    cost: number;
    marketValue: number;
    unrealized: number;
    unrealizedPct: number;
  };
}

export interface NewsItem {
  link: string;
  title: string;
  source: string | null;
  publishedAt: number | null;
}

export type Sentiment = "bullish" | "neutral" | "bearish";
export type AnalysisSignal = "buy" | "hold" | "sell";
/** Scope of an analysis — drives its schedule, the data it sees, and the prompt. */
export type AnalysisKind = "premarket" | "intraday" | "longterm";

export interface Analysis {
  id: number;
  createdAt: number;
  trigger: "scheduled" | "manual";
  kind: AnalysisKind;
  model: string;
  sentiment: Sentiment;
  signal: AnalysisSignal;
  confidence: number;
  summary: string;
  keyPoints: string[];
  priceAtRun: number | null;
}
